'use client';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useContext, useEffect, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Backdrop from '@mui/material/Backdrop';
import { createContext } from 'react';
import { api } from '@ifa/trpc-client';

import { SuccessModal } from '../patientdataform/SuccessModal';

export const DeleteModalContext = createContext<{
    idToDelete: number | null;
    setIdToDelete: (id: number | null) => void;
}>({
    idToDelete: null,
    setIdToDelete: () => void 0,
});

export function DeleteModalContextProvider(props: { children: React.ReactNode }) {
    const [idToDelete, setIdToDelete] = useState<number | null>(null);

    return (
        <DeleteModalContext.Provider
            value={{
                idToDelete,
                setIdToDelete,
            }}
        >
            {props.children}
        </DeleteModalContext.Provider>
    );
}

export function DeleteModal() {
    const { idToDelete, setIdToDelete } = useContext(DeleteModalContext);
    const [open, setOpen] = useState<boolean>(!!idToDelete);
    const [hasPendingSubmission, setHasPendingSubmission] = useState(false);
    const deleteRequest = api.submissionDetail.delete.useMutation();

    useEffect(() => {
        if (idToDelete) {
            setOpen(true);
        }
    }, [idToDelete]);

    const onCloseModal = () => {
        setOpen(false);
        setIdToDelete(null);
    };

    const handleDelete = async () => {
        setHasPendingSubmission(true);
        if (!idToDelete) {
            return;
        }
        try {
            await deleteRequest.mutateAsync({
                id: idToDelete,
            });
        } catch (e) {
            console.error('Error deleting request', e);
        }
        setOpen(false);
        setHasPendingSubmission(false);
        setIdToDelete(null);
    };

    return (
        <>
            <Dialog open={open} onClose={onCloseModal} maxWidth={'sm'}>
                <DialogTitle sx={{ mt: 2 }}>
                    Delete request
                    <IconButton
                        onClick={onCloseModal}
                        aria-label="close"
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>Are you sure you want to delete the selected form request?</DialogContentText>
                </DialogContent>
                <DialogActions sx={{ mb: 2, mr: 2, ml: 2, justifyContent: 'center' }}>
                    <Button variant="outlined" onClick={() => onCloseModal()} disabled={hasPendingSubmission}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            handleDelete();
                        }}
                        data-cy="submitDeleteRequest"
                        disabled={hasPendingSubmission}
                    >
                        Delete
                    </Button>
                    <Backdrop
                        title="backdropSpinner"
                        sx={{ color: '#fff', zIndex: 2 }}
                        open={hasPendingSubmission}
                        data-cy="backdropSpinner"
                    >
                        <CircularProgress title="spinner" color="inherit" />
                    </Backdrop>
                </DialogActions>
            </Dialog>
            {deleteRequest.isSuccess ? (
                <SuccessModal success={deleteRequest.isSuccess} title="Successfully deleted">
                    The selected form request was successfully deleted.
                </SuccessModal>
            ) : null}
        </>
    );
}
